import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";

import { useConnections, useSystems } from "../../api/hooks";
import type { Connection, RevokeState } from "../../api/types";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Page } from "../../components/Page";
import { Spinner } from "../../components/Spinner";
import { SystemWarningBanner } from "../../components/SystemWarningBanner";
import { VendorMark } from "../../components/VendorMark";
import { WizardRail, type WizardRailStep } from "../../components/WizardRail";
import { vendorName } from "../../i18n/oauthCopy";
import { formatRelativePast } from "../../i18n/relativeTime";
import { t } from "../../i18n/t";
import { clearDraft } from "./newMigrationDraft";

/**
 * §B2.1/§B2.2/§B2.4, redesigned as NewMigration.html (guide §5, **5.3 changes the flow**): steps 1 and 2 are
 * ONE page now — the source and destination radio lists side by side, with the summary bar and "Continue to
 * consent" below. The destination list disables the connection already chosen as the source ("Already the
 * source"); the same SYSTEM on both sides is still refused (`same_system_error`, the cross-check the server
 * repeats at `create_migration`).
 *
 * Two things still make this screen more than a pair of lists:
 *
 * 1. **The acceptance-gate banner is server-driven and not dismissible** (§B2.3, §A8.6). It sits above the
 *    pickers and its words come from `list_systems().warnings`, so an operator cannot click past "do not
 *    migrate a live client" and a change to the gate is a change on the server, not a redeploy of the bundle.
 * 2. **There is no connect-new affordance here any more.** Every system — Xero included — connects from
 *    `/connections/add` (the dashed link under each list). The old inline Xero form is gone with the rest:
 *    a mid-wizard OAuth round trip could not keep the other side of the pair anyway (`_connections_return`
 *    lands every callback on `/connections?connected=<system>`), and Xero's own return_to from
 *    `/connections/add` is `/connections`. The fake connector is dev tooling, so it appears in the pickers
 *    only when the server reports `dev_mode` (§B2.4, v2-H-7).
 */

const FAKE_SYSTEM = "fake";

/** Every system `list_systems` reports for this role, minus `fake` — which is dev tooling, gated on dev_mode. */
function realSystems(list: readonly string[] | undefined): string[] {
  return (list ?? []).filter((system) => system !== FAKE_SYSTEM);
}

/**
 * Fable F8: this used to also require `connection.kind === kind` (`"source"`/`"destination"`), but no
 * OAuth-created connection ever has that `kind` - `xero_oauth_callback`/`qbo_oauth_callback`/
 * `freeagent_oauth_callback`/`sage_bc_oauth_callback`/`lucibook_oauth_callback` all write `kind="oauth"`
 * (`routers/connections.py`). A freshly OAuth-connected Xero/QBO/FreeAgent/Sage BC/LuciBook row therefore
 * never matched `kind === "source"` or `kind === "destination"` and never showed up here at all. Which
 * SIDE a connection can play is which of `sourceSystems`/`destinationSystems` (both `list_systems()`-derived,
 * the same source `AddConnectionScreen` reads) its `system` belongs to - never its `kind`, which only ever
 * distinguishes an OAuth connection from a manually-created one and says nothing about role.
 */
export function pick(connections: readonly Connection[], systems: readonly string[]): Connection[] {
  return connections.filter(
    (connection) =>
      systems.includes(connection.system) &&
      connection.revoke_state !== "revoked" &&
      // Round 4 (Opus verification review, MEDIUM): `revoke_failed` used to fall through this filter
      // unchallenged - only a CONFIRMED `revoked` was excluded - so a Xero or LuciBook connection whose
      // revoke failed (credentials kept on purpose, so "Try revoking again" can retry) stayed pickable
      // for a brand-new migration too, which would only fail later at extraction. `_check_connection`
      // (`api/routers/migrations.py`) enforces the same rule server-side; this is the picker mirroring it.
      connection.revoke_state !== "revoke_failed",
  );
}

/** The dev-only `fake` connector is the one case where `kind` DOES mean role: `kind: "source"` or
 * `kind: "destination"` is written explicitly at creation (AddConnectionScreen's dev panel), and both fake
 * connections share the same `system: "fake"` - `pick`'s system-based filter alone cannot tell them apart. */
export function pickFake(connections: readonly Connection[], kind: string): Connection[] {
  return connections.filter(
    (connection) =>
      connection.system === FAKE_SYSTEM &&
      connection.kind === kind &&
      connection.revoke_state !== "revoked" &&
      connection.revoke_state !== "revoke_failed",
  );
}

/**
 * What the operator needs to tell connections apart: WHICH vendor and WHICH organisation (never the sign-in
 * e-mail, which is the same for every Xero org one operator authorised). `org` is the vendor's organisation
 * name (`tenant_name`/`organisation_name`); older LuciBook rows have none, so they fall back to the org id
 * rather than to the base URL every LuciBook row shares. `detail` is the muted tail on the vendor line: for
 * LuciBook the host it points at, and for any system a `display_name` that adds something beyond the org name.
 */
export function describeConnection(connection: Connection): { vendor: string; org: string; detail: string } {
  const vendor = vendorName(connection.system);
  const named = (connection.tenant_name ?? connection.organisation_name ?? "").trim();
  if (connection.system === "lucibook") {
    return {
      vendor,
      org: named !== "" ? named : t("connect.lucibook_org_fallback", { org_id: connection.org_id }),
      detail: hostOf(connection.display_name),
    };
  }
  const org = named !== "" ? named : connection.display_name;
  return { vendor, org, detail: connection.display_name !== org ? connection.display_name : "" };
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** The right-hand meta of a pick card (handoff §4: created_at). A connection that needs reconnecting says so
 * in words here; anything else reads "Connected {when}". */
function connectionMeta(connection: Connection): string {
  const revokeState = (connection.revoke_state ?? "active") as RevokeState;
  const status = connection.status ?? "connected";
  if (revokeState === "active" && status === "reconnect_needed") {
    return t("connections.status.reconnect_needed");
  }
  return t("connect.meta_connected", { when: formatRelativePast(connection.created_at) });
}

function wizardSteps(): readonly WizardRailStep[] {
  return [
    { label: t("connect.rail_source"), state: "current" },
    { label: t("connect.rail_destination"), state: "current" },
    { label: t("connect.rail_basis"), state: "upcoming" },
    { label: t("connect.rail_preview"), state: "upcoming" },
  ];
}

/** The wizard's breadcrumb: "Migrations / New migration" (NewMigration.html, Consent.html). */
export function WizardBreadcrumb(): JSX.Element {
  return (
    <nav className="breadcrumb" aria-label={t("a11y.breadcrumb")}>
      <Link to="/">{t("operator.title")}</Link> <span aria-hidden="true">/</span> {t("connect.breadcrumb")}
    </nav>
  );
}

export function ConnectScreen(): JSX.Element {
  const navigate = useNavigate();
  const systems = useSystems();
  const connections = useConnections();

  const [sourceId, setSourceId] = useState<string | null>(null);
  const [destinationId, setDestinationId] = useState<string | null>(null);

  const sourceSystems = useMemo(() => realSystems(systems.data?.sources), [systems.data]);
  const destinationSystems = useMemo(() => realSystems(systems.data?.destinations), [systems.data]);
  const devMode = systems.data?.dev_mode === true;

  const rows = useMemo(() => connections.data ?? [], [connections.data]);
  const sources = useMemo(
    () => [...pick(rows, sourceSystems), ...(devMode ? pickFake(rows, "source") : [])],
    [rows, sourceSystems, devMode],
  );
  const destinations = useMemo(
    () => [...pick(rows, destinationSystems), ...(devMode ? pickFake(rows, "destination") : [])],
    [rows, destinationSystems, devMode],
  );

  const chosenSource = sources.find((c) => c.id === sourceId) ?? null;
  const chosenDestination = destinations.find((c) => c.id === destinationId) ?? null;

  // §deliverable 2's cross-check: the same system on both sides of one migration is never a valid choice,
  // even though two DIFFERENT connections to it (two QBO orgs, say) both individually qualify. The same
  // CONNECTION on both sides is handled by the destination list itself (disabled, "Already the source").
  const sameSystem =
    chosenSource !== null && chosenDestination !== null && chosenSource.system === chosenDestination.system;
  const sameConnection = chosenSource !== null && chosenSource.id === chosenDestination?.id;

  function onContinue(): void {
    if (chosenSource === null || chosenDestination === null || sameSystem || sameConnection) return;
    clearDraft(); // §B1.5: cleared on navigating away from the connect step.
    navigate("/migrations/new/consent", {
      state: {
        source_system: chosenSource.system,
        destination_system: chosenDestination.system,
        source_connection_id: chosenSource.id,
        destination_connection_id: chosenDestination.id,
      },
    });
  }

  if (systems.isPending || connections.isPending) return <Spinner />;

  return (
    <Page
      title={t("connect.title")}
      breadcrumb={<WizardBreadcrumb />}
      headerBottom={<WizardRail label={t("connect.rail_label")} steps={wizardSteps()} />}
    >
      <SystemWarningBanner
        warnings={systems.data?.warnings}
        systems={[...sourceSystems, ...destinationSystems]}
      />

      <div className="connectors">
        <ConnectionPicker
          legendId="connect-source"
          stepEyebrow={t("connect.step_1of4")}
          role={t("connect.source")}
          name="source"
          connections={sources}
          chosenId={sourceId}
          disabledId={null}
          onChoose={setSourceId}
          addLabel={t("connect.add_source_link")}
        />
        <div className="connectors__arrow" aria-hidden="true">
          <span className="connectors__arrow-ring">
            <ArrowRight size={16} strokeWidth={2} />
          </span>
        </div>
        <ConnectionPicker
          legendId="connect-destination"
          stepEyebrow={t("connect.step_2of4")}
          role={t("connect.destination")}
          name="destination"
          connections={destinations}
          chosenId={destinationId}
          disabledId={sourceId}
          onChoose={setDestinationId}
          addLabel={t("connect.add_destination_link")}
        />
      </div>

      {sameSystem && chosenSource !== null ? (
        <Banner tone="warn">
          {t("connect.same_system_error", { vendor: vendorName(chosenSource.system) })}
        </Banner>
      ) : null}

      <div className="connectsummary">
        <span className="connectsummary__pair">
          {chosenSource === null || chosenDestination === null ? null : (
            <span className="connectsummary__pair" data-testid="connect-summary">
              <VendorMark system={chosenSource.system} size={24} />
              <strong>{describeConnection(chosenSource).org}</strong>
              <span className="connectsummary__arrow" aria-hidden="true">
                →
              </span>
              <VendorMark system={chosenDestination.system} size={24} />
              <strong>{describeConnection(chosenDestination).org}</strong>
            </span>
          )}
        </span>
        <Button
          variant="primary"
          onClick={onContinue}
          disabled={chosenSource === null || chosenDestination === null || sameSystem || sameConnection}
        >
          {t("connect.continue")}
          <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
        </Button>
      </div>
    </Page>
  );
}

/** One side of the pair: the fieldset with its step legend, the radio cards, and the dashed add link. */
function ConnectionPicker({
  legendId,
  stepEyebrow,
  role,
  name,
  connections,
  chosenId,
  disabledId,
  onChoose,
  addLabel,
}: {
  legendId: string;
  stepEyebrow: string;
  role: string;
  name: string;
  connections: readonly Connection[];
  chosenId: string | null;
  /** The connection already chosen on the other side: disabled here, with "Already the source" (5.3). */
  disabledId: string | null;
  onChoose: (id: string) => void;
  addLabel: string;
}): JSX.Element {
  return (
    <fieldset className="picklist" aria-labelledby={legendId}>
      <legend className="picklegend" id={legendId}>
        <span className="eyebrow">{stepEyebrow}</span>
        <span className="picklegend__role">{role}</span>
      </legend>
      {connections.map((connection) => {
        const disabled = connection.id === disabledId;
        const { vendor, org, detail } = describeConnection(connection);
        return (
          <label key={connection.id} className="pick">
            <input
              type="radio"
              name={name}
              value={connection.id}
              checked={chosenId === connection.id}
              disabled={disabled}
              onChange={() => onChoose(connection.id)}
            />
            <VendorMark system={connection.system} size={32} />
            <span className="pick__main">
              <span className="pick__name">{org}</span>
              <span className="pick__sub">{detail === "" ? vendor : `${vendor} · ${detail}`}</span>
            </span>
            <span className="pick__meta" data-live>
              {disabled ? t("connect.already_source") : connectionMeta(connection)}
            </span>
          </label>
        );
      })}
      <Link className="pickadd" to="/connections/add">
        <Plus size={16} strokeWidth={2} aria-hidden="true" />
        {addLabel}
      </Link>
    </fieldset>
  );
}
